// Sanitized markdown → HTML for the `markdown` widget kind (arc 101 slice 09,
// task #81). Widget content is agent-authored (via `dashboard_upsert`) or seeded,
// so it is UNTRUSTED: the sanitize pass here is load-bearing, not cosmetic.
//
// Two lines of defence:
//   1. markdown-it with `html: false` — raw HTML in the source is emitted as
//      escaped TEXT, never parsed. So the only tags in the output are the ones
//      markdown-it itself generates (a fixed, safe set), and link/image URLs pass
//      its built-in `validateLink` (blocks `javascript:`/`vbscript:`/`data:`).
//   2. A DOM allow-list pass over that output — unknown elements are flattened to
//      their text, every non-allow-listed attribute (incl. all `on*` handlers) is
//      dropped, and `href`s are re-checked against a safe-scheme allow-list. This
//      is defence-in-depth: even if markdown-it ever regressed, nothing scriptable
//      reaches the DOM.
//
// mermaid: a ```mermaid fence becomes an inert `<pre class="mermaid">` mount node
// with its source ESCAPED (no script). A later lazy-mermaid hydration pass can run
// `mermaid.run()` over `.mermaid` nodes with zero change here; until then the
// diagram source renders as sanitized preformatted text. We deliberately do NOT
// pull the heavy mermaid runtime into the SPA bundle in this task.

import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({ html: false, linkify: true, typographer: false });

// Fence override: mermaid → inert mount node; every other fence renders normally.
const renderFence = md.renderer.rules.fence?.bind(md.renderer);
md.renderer.rules.fence = (tokens, idx, options, env, self) => {
  const token = tokens[idx];
  if (token && token.info.trim().toLowerCase() === 'mermaid') {
    return `<pre class="mermaid dashboard-md__mermaid">${md.utils.escapeHtml(token.content)}</pre>\n`;
  }
  return renderFence
    ? renderFence(tokens, idx, options, env, self)
    : self.renderToken(tokens, idx, options);
};

/** Tag → the attributes it may keep. Any tag absent here is flattened to its
 * text; any attribute absent from its list is dropped. `class` survives only on
 * the code scaffold (the mermaid mount + `language-*` fences — inert strings).
 *
 * INTENTIONAL OMISSIONS:
 *   - `img`: dropped intentionally. Images in agent-authored widget content pose
 *     SSRF/tracking/attribution risks; beacon pixels and privacy concerns outweigh
 *     inline diagram benefits. Mermaid fences (```mermaid) provide structured diagrams
 *     without the risks of arbitrary external images.
 */
const ALLOWED: Readonly<Record<string, readonly string[]>> = {
  a: ['href'],
  p: [],
  br: [],
  em: [],
  strong: [],
  del: [],
  code: ['class'],
  pre: ['class'],
  blockquote: [],
  ul: [],
  ol: [],
  li: [],
  h1: [],
  h2: [],
  h3: [],
  h4: [],
  h5: [],
  h6: [],
  hr: [],
  table: [],
  thead: [],
  tbody: [],
  tr: [],
  th: [],
  td: [],
};

/** An href is safe if it has no scheme (relative / anchor) or a known-good one. */
function safeUrl(value: string): boolean {
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value.trim());
  return scheme === null || /^(https?|mailto)$/i.test(scheme[1] ?? '');
}

/** Recursively enforce the allow-list on an element's subtree, in place. */
function scrub(node: Element): void {
  for (const child of Array.from(node.children)) {
    const tag = child.tagName.toLowerCase();
    const allowedAttrs = ALLOWED[tag];
    if (allowedAttrs === undefined) {
      // Unknown element (can't arise from `html:false`, but belt-and-suspenders):
      // replace it with its text so nothing scriptable can survive.
      child.replaceWith(child.ownerDocument.createTextNode(child.textContent ?? ''));
      continue;
    }
    for (const attr of Array.from(child.attributes)) {
      const name = attr.name.toLowerCase();
      if (!allowedAttrs.includes(name) || (name === 'href' && !safeUrl(attr.value))) {
        child.removeAttribute(attr.name);
      }
    }
    if (tag === 'a' && child.hasAttribute('href')) {
      child.setAttribute('rel', 'noopener noreferrer nofollow');
      child.setAttribute('target', '_blank');
    }
    scrub(child);
  }
}

/**
 * Render untrusted markdown to a SANITIZED HTML string safe to assign via
 * `innerHTML`. Prose + mermaid mounts survive; scripts, raw HTML, event handlers,
 * and unsafe URLs do not.
 */
export function renderMarkdown(content: string): string {
  // Parse the FRAGMENT (no `<body>` wrapper) — the parser auto-creates the body
  // and places the block content in it. An explicit wrapper would be re-parsed as
  // a nested <body> element, which the allow-list would then flatten away.
  const doc = new DOMParser().parseFromString(md.render(content), 'text/html');
  const body = doc.body;
  if (!body) return '';
  scrub(body);
  return body.innerHTML;
}
